import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import HookForm from '../../../../components/HookForm/HookForm';
import Button from '../../../../components/Button/Button';
import Loader from '../../../../components/Loader/Loader';
import LoaderInline from '../../../../components/Loader/LoaderInline';
import formStyles from '../../../../components/Form/form.module.css';

import SellerBlock from './SellerBlock';
import { buildCarInfoInputs } from '../carInfoInputs';

import createCar from '../../../../api/cars/createCar';
import createConsignment from '../../../../api/cars/createConsignment';
import getDcRequisites from '../../../../api/cars/getDcRequisites';
import ensureContainer from '../../../../api/cars/ensureContainer';
import getItems from '../../../../api/cars/getItems';

import alert from '../../../../helpers/alert';
import extractErrorMessage from '../../../../utils/extractErrorMessage';
import { CAR_ORIGIN_TYPES } from '../../../../constants/carOrigin';
import { textStyles } from '../../../../constants/textStyles';
import {
  REQUISITES_FIELDS,
  REQUISITES_INPUTS,
  SELLER_REQUISITES_INPUTS,
  cleanBank,
  sellerFio,
} from './requisitesInputs';

const carGrid = { display: 'grid', gap: '20px', gridTemplateColumns: '1fr 1fr 1fr 1fr', alignItems: 'flex-end' };
const reqGrid = { display: 'grid', gap: '20px', gridTemplateColumns: '1fr 1fr 1fr' };
const reqOverlay = {
  position: 'absolute',
  inset: 0,
  zIndex: 5,
  background: 'rgba(255, 255, 255, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/**
 * Постановка б/у автомобиля по договору поручения — один экран без шагов:
 * данные ТС, продавец (клиент CRM), реквизиты ДЦ и счёт продавца.
 * После сохранения менеджер сразу переходит к созданию сделки.
 */
const ConsignmentCreate = ({ originInput }) => {
  const navigate = useNavigate();

  const carFormRef = useRef(null);
  const reqFormRef = useRef(null);
  const sellerReqFormRef = useRef(null);
  const sellerRef = useRef(null);

  const [containerId, setContainerId] = useState(null);
  const [seller, setSeller] = useState(null);
  const [sellerError, setSellerError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingRequisites, setLoadingRequisites] = useState(false);
  const [carId, setCarId] = useState(null);

  // Реквизиты берём ровно у выбранного ДЦ автомобиля — ничего не подставляем
  // по ДЦ менеджера, пока менеджер сам не выбрал ДЦ.
  const handleDcChange = async (selected) => {
    const list = Array.isArray(selected) ? selected : [selected];
    const dcId = list[list.length - 1]?.value;
    if (!dcId) return;

    setLoadingRequisites(true);
    const res = await getDcRequisites(dcId).catch(() => null);
    const requisites = res?.data || {};
    Object.entries(REQUISITES_FIELDS).forEach(([field, key]) =>
      reqFormRef.current?.setValue(field, requisites?.[key] ?? '')
    );
    setLoadingRequisites(false);
  };

  // Комплектации висят на контейнере (пара марка+модель), а у б/у-марок и
  // моделей, заведённых менеджером здесь же, контейнера ещё нет — бэк отдаёт
  // существующий или заводит недостающий.
  // ponytail: контейнер заводится сразу при выборе модели, поэтому брошенная
  // форма оставляет пустой контейнер. В витрину он не попадает (там фильтр по
  // стоку), чистить — только если такие записи начнут мешать в /cars/containers.
  const resolveContainerId = async (modelOption, markOption) => {
    if (!markOption?.value || !modelOption?.value) {
      setContainerId(null);
      return null;
    }
    try {
      const res = await ensureContainer({ markId: markOption.value, modelId: modelOption.value });
      const id = res?.data?.id ?? null;
      setContainerId(id);
      return id;
    } catch (e) {
      // Молча глотать нельзя: без контейнера пустеет список комплектаций и
      // пропадает кнопка «Добавить комплектацию» — выглядит как сломанная
      // вёрстка, хотя на деле не ответил бэк.
      setContainerId(null);
      alert.error(
        extractErrorMessage(e?.response?.data) ||
          'Не удалось определить контейнер по марке и модели — комплектации недоступны'
      );
      return null;
    }
  };

  const carInputs = useMemo(
    () =>
      buildCarInfoInputs({
        onMarkChange: () => setContainerId(null),
        onModelChange: resolveContainerId,
        onDcChange: handleDcChange,
        singleDc: true,
        usedDcOnly: true,
        getContainerId: () => containerId,
        withTechPassport: true,
        mileageRequired: true,
        usedDictionaries: true,
      }),
    [containerId]
  );

  const validateCar = (values, resolvedContainerId) => {
    if (values?.vin?.trim()?.length !== 17) {
      alert.error('Длина VIN должна быть 17 символов');
      return false;
    }
    if (values?.issueYear > new Date().getFullYear() || values?.issueYear < 1900) {
      alert.error('Проверьте год выпуска авто');
      return false;
    }
    if (!resolvedContainerId) {
      alert.error('Не удалось определить контейнер по выбранной марке/модели');
      return false;
    }
    return true;
  };

  const saveCar = async (values, resolvedContainerId) => {
    if (carId) return carId;

    const payload = {
      phid: 5,
      originCarId: CAR_ORIGIN_TYPES.CONSIGNMENT,
      used: true,
      containerId: resolvedContainerId,
      markId: values?.mark?.value,
      modelId: values?.model?.value,
      complectationId: values?.complectationId?.value,
      transmissionId: values?.transmissionId?.value,
      issueYear: values?.issueYear,
      vin: values?.vin?.trim(),
      color: values?.color?.value,
      price: parseInt(String(values?.price).replace(/[^\d]/g, ''), 10),
      engine_volume: values?.engine_volume,
      powerId: values?.power,
      wdId: values?.wdId?.value,
      cityId: values?.cityId?.value,
      dcIds: [].concat(values?.dc || []).map((dc) => dc?.value).filter((v) => v != null),
      mileage: values?.mileage ? parseInt(values.mileage, 10) : undefined,
      engineType: values?.engineType?.value,
      texNumber: values?.texNumber,
      texDate: values?.texDate ? dayjs(values.texDate).format('YYYY-MM-DD') : undefined,
      paid: 5124,
      paidTypeId: 1,
      countryId: 113,
    };

    const data = new FormData();
    data.append('json', JSON.stringify(payload));

    const response = await createCar(data);
    const id = response?.data?.car?.id || response?.data?.id;
    setCarId(id);
    return id;
  };

  const handleSubmit = async () => {
    if (!seller?.value) {
      setSellerError('Выберите или создайте продавца');
      sellerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSellerError('');

    const sellerRequisites = await sellerReqFormRef.current.submit();
    if (!sellerRequisites) return;

    const carValues = await carFormRef.current.submit();
    if (!carValues) return;

    // Менеджер может нажать «Сохранить» раньше, чем ответит ensureContainer по
    // только что выбранной модели — дожимаем контейнер здесь, а не падаем в
    // «не удалось определить».
    const resolvedContainerId = containerId || (await resolveContainerId(carValues?.model, carValues?.mark));
    if (!validateCar(carValues, resolvedContainerId)) return;

    const requisites = await reqFormRef.current.submit();
    if (!requisites) return;

    setSaving(true);
    try {
      const id = await saveCar(carValues, resolvedContainerId);
      if (!id) throw new Error('Не удалось создать автомобиль');

      await createConsignment({
        carId: id,
        sellerClientId: seller.value,
        price: parseInt(String(carValues?.price).replace(/[^\d]/g, ''), 10),
        requisites,
        sellerRequisites: {
          recipient: sellerRequisites.recipient,
          iban: cleanBank(sellerRequisites.iban),
          bankName: sellerRequisites.bankName,
          bic: cleanBank(sellerRequisites.bic),
        },
      });

      await goToDeal(id);
    } catch (e) {
      alert.error(extractErrorMessage(e?.response?.data) || e?.message || 'Не удалось оформить поручение');
    } finally {
      setSaving(false);
    }
  };

  // Сразу уводим в создание сделки с этим авто. Карточку тянем с бэка, чтобы
  // экран сделки получил ровно тот же объект, что и при продаже из карточки авто.
  const goToDeal = async (id) => {
    const res = await getItems({ id }).catch(() => null);
    const car = res?.data?.cars?.[0];

    if (!car) {
      alert.success('Автомобиль принят по договору поручения');
      navigate(`/cars/car/${id}/main-data`);
      return;
    }

    navigate('/deals/create', {
      state: {
        car: {
          ...car,
          consignment: car?.consignment || { seller: seller?.client || { name: seller?.label } },
        },
      },
    });
  };

  return (
    <div style={{ paddingBottom: 20 }}>
      {/* Форму не размонтируем на время сохранения: при ошибке бэка введённое
          не должно пропадать. */}
      {saving && <Loader />}

      {/* Происхождение остаётся наверху, где менеджер его и выбрал. */}
      {originInput && <HookForm border inputs={[originInput]} grid={{ display: 'grid', maxWidth: 420 }} />}

      <div className={formStyles.borderWrapper} ref={sellerRef}>
        <p style={textStyles.formTitle}>1. Продавец</p>
        <div style={{ marginTop: 20 }}>
          <SellerBlock
            seller={seller}
            onChange={(value) => {
              setSeller(value);
              setSellerError('');
              const fio = sellerFio(value);
              if (fio) sellerReqFormRef.current?.setValue('recipient', fio);
            }}
            error={sellerError}
          />
        </div>
      </div>

      <HookForm
        ref={sellerReqFormRef}
        border
        title="2. Счёт продавца"
        inputs={SELLER_REQUISITES_INPUTS}
        grid={reqGrid}
      />

      <HookForm
        ref={carFormRef}
        border
        title="3. Данные автомобиля"
        inputs={carInputs}
        grid={carGrid}
      />

      {/* Форму не подменяем лоадером: она должна остаться смонтированной, чтобы
          ответ по ДЦ было куда записать. Вместо этого гасим блок оверлеем —
          customDataTop рендерится внутри карточки, поэтому inset считается от неё. */}
      <HookForm
        ref={reqFormRef}
        border
        title="4. Реквизиты дилерского центра"
        inputs={REQUISITES_INPUTS}
        grid={reqGrid}
        style={{ position: 'relative' }}
        customDataTop={
          loadingRequisites
            ? () => (
                <div style={reqOverlay}>
                  <LoaderInline />
                </div>
              )
            : undefined
        }
      />

      <Button text="Сохранить и перейти к сделке" fill onClick={handleSubmit} disabled={saving} />
    </div>
  );
};

export default ConsignmentCreate;
